/**
 * CISO landing page — /app/landing/ciso
 *
 * Threat picture: actors x TTPs x our exposure, KEV, correlations,
 * compliance %, open incidents. All data from existing read endpoints.
 */
import { useEffect, useState, useCallback } from 'react'
import { Skull, Globe, Network, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface Correlation { id: string; severity: string; title?: string; rule_name?: string; created_at?: string }
interface Actor { actor: string; ttp_count?: number; sector_match?: number; exposure?: number }
interface RansomwareVictim { victim: string; sector?: string; date?: string }

export default function CisoLandingPage() {
  const [refreshing, setRefreshing]     = useState(false)
  const [threatLevel, setThreatLevel]   = useState<{ level?: string; score?: number } | null>(null)
  const [actors, setActors]             = useState<Actor[]>([])
  const [kevCount, setKevCount]         = useState<number>(0)
  const [correlations, setCorrelations] = useState<Correlation[]>([])
  const [compliance, setCompliance]     = useState<number | null>(null)
  const [ransomware, setRansomware]     = useState<RansomwareVictim[]>([])
  const [openIncidents, setIncidents]   = useState<number>(0)

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cyber/threat-level')),
      j(api.get('/api/cdm/threat-attribution', { params: { limit: 5 } })),
      j(api.get('/api/cyber/cves', { params: { severity: 'critical', limit: 1 } })),
      j(api.get('/api/cdm/correlations', { params: { state: 'open', severity: 'critical', limit: 8 } })),
      j(api.get('/api/cdm/compliance/scorecard', { params: { framework: 'CIS-v8' } })),
      j(api.get('/api/cyber/feed', { params: { signal_type: 'ransomware', limit: 5 } })),
      j(api.get('/api/incidents', { params: { limit: 1 } })),
    ]).then(([tl, a, k, c, comp, r, i]) => {
      setThreatLevel(tl.data || null)
      setActors(a.data?.actors || a.data?.results || [])
      setKevCount(k.data?.total ?? k.data?.count ?? (k.data?.cves?.length ?? 0))
      setCorrelations(c.data?.correlations || c.data?.results || [])
      setCompliance(comp.data?.percent ?? comp.data?.score_pct ?? null)
      setRansomware(r.data?.items || r.data?.results || [])
      setIncidents(i.data?.open ?? i.data?.count ?? 0)
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const critCorr = correlations.length

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="CISO Landing"
        subtitle="threat picture · actors · KEV · correlations · compliance"
        persona="ciso"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Threat level"
          value={threatLevel?.level ? threatLevel.level.toUpperCase() : '—'}
          tone={threatLevel?.level === 'critical' ? 'rose'
                : threatLevel?.level === 'elevated' ? 'amber'
                : threatLevel?.level === 'guarded'  ? 'cyan'
                : 'emerald'}
          to="/app/cyber" />
        <HeroTile label="Active actors" value={safeNum(actors.length)} tone="amber"
          sub="targeting our sector" to="/app/threat-actors" />
        <HeroTile label="Critical KEV" value={safeNum(kevCount)}
          tone={kevCount > 0 ? 'rose' : 'emerald'} to="/app/cyber" />
        <HeroTile label="Open correlations" value={safeNum(critCorr)}
          tone={critCorr > 0 ? 'rose' : 'emerald'} sub="critical · open" to="/app/cdm/correlations" />
        <HeroTile label="Compliance %"
          value={compliance != null ? `${compliance.toFixed(0)}%` : '—'}
          tone={compliance == null ? 'muted' : compliance >= 90 ? 'emerald' : compliance >= 75 ? 'amber' : 'rose'}
          sub="CIS-v8" to="/app/cdm/compliance" />
        <HeroTile label="Open incidents" value={safeNum(openIncidents)}
          tone={openIncidents > 0 ? 'amber' : 'emerald'} to="/app/incidents" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Live correlations · critical" viewAllTo="/app/cdm/correlations">
              {correlations.length === 0 ? (
                <EmptyState message="No open critical correlations."
                  configureTo="/app/cdm/correlation-rules" configureLabel="manage rules" />
              ) : (
                <div className="space-y-1">
                  {correlations.slice(0, 5).map(c => (
                    <ListRow key={c.id}
                      to={`/app/cdm/correlations`}
                      primary={c.title || c.rule_name || c.id}
                      secondary={c.created_at}
                      right={<span className="text-rose-light">{c.severity}</span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Top actors targeting us · TTPs" viewAllTo="/app/cdm/attribution">
              {actors.length === 0 ? (
                <EmptyState message="Threat-attribution lens has no data yet."
                  configureTo="/app/cdm/attribution" />
              ) : (
                <div className="space-y-1">
                  {actors.slice(0, 5).map(a => (
                    <ListRow key={a.actor}
                      to={`/app/actor/${encodeURIComponent(a.actor)}`}
                      primary={a.actor}
                      secondary={`${a.ttp_count ?? 0} TTPs · ${a.sector_match ?? 0} sector match`}
                      right={a.exposure !== undefined ? `exposure ${a.exposure}` : ''} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Compliance scorecard" viewAllTo="/app/cdm/compliance">
              {compliance == null ? (
                <EmptyState message="Compliance scorecard not computed yet."
                  configureTo="/app/cdm/compliance" />
              ) : (
                <div className="text-center py-3">
                  <div className={`text-3xl font-bold font-mono ${
                    compliance >= 90 ? 'text-emerald'
                      : compliance >= 75 ? 'text-amber-light'
                      : 'text-rose-light'
                  }`}>{compliance.toFixed(1)}%</div>
                  <div className="text-[10px] text-text-muted mt-1">CIS-v8 controls passing</div>
                </div>
              )}
            </Panel>

            <Panel title="Recent ransomware in our sector" viewAllTo="/app/ransomware">
              {ransomware.length === 0 ? (
                <EmptyState message="No recent ransomware victims observed." />
              ) : (
                <div className="space-y-1">
                  {ransomware.slice(0, 5).map((r, i) => (
                    <ListRow key={i}
                      primary={r.victim}
                      secondary={r.sector}
                      right={r.date} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Open Investigation', icon: AlertTriangle, tone: 'primary', to: '/app/cdm' },
        { label: 'Threat Globe',       icon: Globe,         tone: 'secondary', to: '/app/threat-globe' },
        { label: 'Correlations',       icon: Network,       tone: 'ghost',     to: '/app/cdm/correlations' },
        { label: 'Cyber War Room',     icon: Skull,         tone: 'ghost',     to: '/app/cyber-warroom' },
      ]} />
    </div>
  )
}
